import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { create, isPlayerSupported } from 'amazon-ivs-player';
import { useTranslate } from '@refinedev/core';
import { createLogger } from '../../utils/logger';

const logger = createLogger('IVSPlayer');

interface IVSPlayerProps {
  streamUrl: string | null;
  isLive?: boolean;
  onPlayerLoad?: () => void;
  onPlayerError?: (error: string) => void;
  onPlayerStateChange?: (state: string) => void;
  style?: React.CSSProperties;
  controls?: boolean;
  autoplay?: boolean;
}

export const IVSPlayer: React.FC<IVSPlayerProps> = ({
  streamUrl,
  isLive = true,
  onPlayerLoad,
  onPlayerError,
  onPlayerStateChange,
  style = {},
  controls = false,
  autoplay = true
}) => {
  const translate = useTranslate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const initializationIdRef = useRef<number>(0); // Track initialization to handle race conditions
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [dots, setDots] = useState('');
  const [isStreamPreparing, setIsStreamPreparing] = useState(false); // 404 = stream not ready yet

  // Stocker les callbacks dans des refs pour éviter les re-renders infinis
  const onPlayerLoadRef = useRef(onPlayerLoad);
  const onPlayerErrorRef = useRef(onPlayerError);
  const onPlayerStateChangeRef = useRef(onPlayerStateChange);

  // Mettre à jour les refs quand les props changent
  useEffect(() => {
    onPlayerLoadRef.current = onPlayerLoad;
  }, [onPlayerLoad]);

  useEffect(() => {
    onPlayerErrorRef.current = onPlayerError;
  }, [onPlayerError]);

  useEffect(() => {
    onPlayerStateChangeRef.current = onPlayerStateChange;
  }, [onPlayerStateChange]);

  // Animation des points de chargement
  useEffect(() => {
    if (isLoading && !isConnected) {
      const interval = setInterval(() => {
        setDots(prev => (prev === '...' ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLoading, isConnected]);

  // Callbacks stables pour les événements IVS (utilisent les refs)
  const handleReady = useCallback(() => {
    logger.log('🎬 Player READY event fired', { streamUrl: streamUrl?.substring(0, 50) });
    autoRetryCountRef.current = 0; // Reset auto-retry count on success
    setIsLoading(false);
    setError(null);
    setIsStreamPreparing(false);
    onPlayerLoadRef.current?.();
  }, [streamUrl]);

  const handleStateChange = useCallback((event: any) => {
    const state = event.state;
    logger.log('🔄 Player STATE changed:', state, {
      isConnected,
      isLoading,
      streamUrl: streamUrl?.substring(0, 50)
    });
    onPlayerStateChangeRef.current?.(state);

    switch (state) {
      case 'Playing':
        logger.log('▶️ Player now PLAYING');
        autoRetryCountRef.current = 0; // Reset auto-retry count on successful playback
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
        setIsStreamPreparing(false);
        break;
      case 'Buffering':
        logger.log('⏳ Player BUFFERING');
        // Ne pas mettre isLoading à true pour éviter le flickering
        break;
      case 'Idle':
        logger.log('⏸️ Player IDLE');
        setIsConnected(false);
        break;
      case 'Ended':
        logger.log('⏹️ Player ENDED');
        setIsConnected(false);
        setIsLoading(false);
        break;
    }
  }, [isConnected, isLoading, streamUrl]);

  // Track auto-retry attempts for 404 errors (stream not ready yet)
  const autoRetryCountRef = useRef(0);
  const autoRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleError = useCallback((event: any) => {
    const is404Error = event.code === 404 ||
                       event.type?.includes('404') ||
                       event.message?.includes('404') ||
                       event.message?.includes('NotAvailable');

    logger.error('❌ Player ERROR:', {
      type: event.type,
      message: event.message,
      code: event.code,
      is404Error,
      autoRetryCount: autoRetryCountRef.current,
      streamUrl: streamUrl?.substring(0, 50)
    });

    // For 404 errors (stream not ready), keep retrying indefinitely with increasing delays
    // This handles the case where backend says isBroadcasting=true but IVS CDN isn't ready yet
    if (is404Error && playerRef.current && streamUrl) {
      autoRetryCountRef.current++;
      setIsStreamPreparing(true); // Show "preparing" message instead of error

      // Exponential backoff: 2s, 3s, 4s, 5s, then cap at 5s
      const delay = Math.min(2000 + (autoRetryCountRef.current - 1) * 1000, 5000);
      logger.log(`🔄 Stream not ready (404), retrying in ${delay}ms (attempt ${autoRetryCountRef.current})...`);

      // Clear any existing retry timeout
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
      }

      autoRetryTimeoutRef.current = setTimeout(() => {
        if (playerRef.current && streamUrl) {
          logger.log('📡 Auto-retry: Reloading stream URL...');
          playerRef.current.load(streamUrl);
        }
      }, delay);
      return; // Don't set error state, we're retrying
    }

    // For non-404 errors, show the error
    const errorMsg = translate('player.playbackError', { type: event.type });
    setError(errorMsg);
    setIsLoading(false);
    setIsConnected(false);
    setIsStreamPreparing(false);
    onPlayerError?.(errorMsg);
  }, [onPlayerError, translate, streamUrl]);

  // Initialisation du player - dépend UNIQUEMENT de streamUrl et autoplay
  useEffect(() => {
    // Increment initialization ID to track this specific initialization
    const currentInitId = ++initializationIdRef.current;
    const initTimestamp = Date.now();
    let isCancelled = false;
    let loadTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let createdPlayer: any = null; // Track the player instance created in this effect

    logger.log('🚀 Player useEffect triggered', {
      initId: currentInitId,
      hasVideoRef: !!videoRef.current,
      streamUrl: streamUrl?.substring(0, 80),
      autoplay,
      timestamp: new Date().toISOString()
    });

    if (!videoRef.current) {
      logger.warn('⚠️ No video element ref available', { initId: currentInitId });
      return;
    }

    if (!isPlayerSupported) {
      logger.error('❌ IVS Player not supported in this browser', { initId: currentInitId });
      const errorMsg = translate('player.notSupported');
      setError(errorMsg);
      onPlayerErrorRef.current?.(errorMsg);
      return;
    }

    // Nettoyer le player existant avant d'en créer un nouveau
    if (playerRef.current) {
      logger.log('🧹 Cleaning up existing player before creating new one', { initId: currentInitId });
      try {
        playerRef.current.pause();
        playerRef.current.delete();
      } catch (e) {
        logger.warn('⚠️ Error during player cleanup:', e, { initId: currentInitId });
      }
      playerRef.current = null;
    }

    // Ne créer le player que si on a une URL valide
    if (!streamUrl) {
      logger.log('⏭️ No stream URL provided, skipping player creation', { initId: currentInitId });
      setIsLoading(false);
      return;
    }

    try {
      logger.log('🎬 Creating new IVS player instance', { initId: currentInitId });
      // Réinitialiser l'état
      setIsLoading(true);
      setError(null);
      setIsConnected(false);
      setIsStreamPreparing(false);

      // Création du player IVS
      const player = create({
        wasmWorker: '/amazon-ivs-wasmworker.min.js',
        wasmBinary: '/amazon-ivs-wasmworker.min.wasm'
      });

      createdPlayer = player; // Store reference to track this specific player
      playerRef.current = player;
      logger.log('✅ IVS player instance created', { initId: currentInitId });

      // Attachement au élément vidéo
      player.attachHTMLVideoElement(videoRef.current);
      logger.log('✅ Video element attached', { initId: currentInitId });

      // Configuration du player
      player.setAutoplay(autoplay);
      player.setMuted(false);
      player.setVolume(0.8);
      logger.log('✅ Player configured', { initId: currentInitId, autoplay, muted: false, volume: 0.8 });

      // Gestionnaires d'événements
      player.addEventListener('PlayerState' as any, handleStateChange);
      player.addEventListener('Error' as any, handleError);
      player.addEventListener('Ready' as any, handleReady);
      logger.log('✅ Event listeners attached', { initId: currentInitId });

      // Chargement de l'URL avec un délai plus long (500ms) pour éviter les race conditions
      // React StrictMode double-invokes effects, so we need to be extra careful
      logger.log('⏳ Scheduling stream load in 500ms...', { initId: currentInitId });
      loadTimeoutId = setTimeout(() => {
        // Triple-check that this initialization is still valid
        if (isCancelled) {
          logger.warn('⚠️ Load cancelled - effect was cleaned up', { initId: currentInitId });
          return;
        }

        if (initializationIdRef.current !== currentInitId) {
          logger.warn('⚠️ Load skipped - newer initialization in progress', {
            initId: currentInitId,
            currentInitId: initializationIdRef.current
          });
          return;
        }

        // Check that our specific player instance is still the current one
        if (playerRef.current !== createdPlayer) {
          logger.warn('⚠️ Player instance was replaced', { initId: currentInitId });
          return;
        }

        // Final safety check - try to access a player method to verify it's not deleted
        try {
          // This will throw if the player was deleted
          const isPaused = player.isPaused();
          logger.log('📡 Loading stream URL (player verified):', streamUrl.substring(0, 80), { initId: currentInitId, isPaused });
          player.load(streamUrl);
          logger.log('✅ Stream load() called, elapsed time:', Date.now() - initTimestamp, 'ms', { initId: currentInitId });
        } catch (e) {
          logger.warn('⚠️ Player was already deleted, cannot load', { initId: currentInitId, error: e });
        }
      }, 500);

    } catch (err) {
      logger.error('❌ Exception during player initialization:', err, { initId: currentInitId });
      const unknownError = translate('player.unknownError');
      setError(err instanceof Error ? err.message : unknownError);
      onPlayerError?.(err instanceof Error ? err.message : unknownError);
    }

    return () => {
      isCancelled = true;
      logger.log('🧹 Player cleanup function called', { initId: currentInitId });

      // Clear any pending load timeout FIRST
      if (loadTimeoutId) {
        clearTimeout(loadTimeoutId);
        loadTimeoutId = null;
        logger.log('⏹️ Cleared pending load timeout', { initId: currentInitId });
      }

      // Clear any pending auto-retry timeout
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
        logger.log('⏹️ Cleared pending auto-retry timeout', { initId: currentInitId });
      }

      // Reset auto-retry count for next initialization
      autoRetryCountRef.current = 0;

      // Only cleanup the player if it's the one we created in this effect
      if (createdPlayer && playerRef.current === createdPlayer) {
        try {
          createdPlayer.pause();
          createdPlayer.delete();
          logger.log('✅ Player cleaned up successfully', { initId: currentInitId });
        } catch (e) {
          logger.warn('⚠️ Error during player cleanup:', e, { initId: currentInitId });
        }
        playerRef.current = null;
      }
      createdPlayer = null;
    };
    // Note: handleStateChange, handleError, handleReady sont stables (useCallback avec [])
    // donc on ne les inclut pas dans les dépendances pour éviter les re-créations inutiles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamUrl, autoplay]);

  const handleRetry = useCallback(() => {
    logger.log('🔄 Retry requested', { retryCount, maxRetries: 3, hasStreamUrl: !!streamUrl });
    if (retryCount < 3 && streamUrl) {
      setRetryCount(prev => prev + 1);
      setError(null);
      setIsLoading(true);
      setIsConnected(false);

      if (playerRef.current) {
        logger.log('📡 Reloading stream URL');
        playerRef.current.load(streamUrl);
      } else {
        logger.warn('⚠️ No player ref available for retry');
      }
    }
  }, [retryCount, streamUrl]);

  const defaultStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    borderRadius: '10px',
    position: 'relative',
    overflow: 'hidden',
    ...style
  };

  if (error) {
    return (
      <Box sx={defaultStyle}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            padding: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('player.connectionError')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
            {error}
          </Typography>
          {retryCount < 3 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleRetry}
              sx={{ backgroundColor: '#333' }}
            >
              {translate('player.retry', { current: retryCount, max: 3 })}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={defaultStyle}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        playsInline
        controls={controls}
      />

      {((isLoading || isStreamPreparing) && !isConnected) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 10
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6">
            {isStreamPreparing
              ? (translate('player.streamPreparing') || 'Préparation du stream...')
              : `${translate('player.connecting')}${dots}`
            }
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc', mt: 1 }}>
            {isStreamPreparing
              ? (translate('player.streamPreparingHint') || 'Le flux vidéo est en cours de préparation. Connexion automatique en cours...')
              : translate('player.loadingStream')
            }
          </Typography>
          {isStreamPreparing && autoRetryCountRef.current > 0 && (
            <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
              Tentative {autoRetryCountRef.current}...
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
